import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../shared/components/PageHeader';
import CompanyPeriodSelector from '../../shared/components/CompanyPeriodSelector';
import { useLanguage } from '../../app/i18n/LanguageContext';
import { useCompanyWriteAccess } from '../../shared/auth/useCompanyWriteAccess';
import ifrsService from '../../services/ifrs';
import { fetchActiveIfrsStandards } from '../../services/ifrsStandards';
import './VukIfrsTransformationWizardPage.css';

const COLLECTOR_TAB_STANDARDS = {
  'fx-rates':          'IAS 21',
  'tax-profiles':      'IAS 12',
  'tax-bases':         'IAS 12',
  'asset-register':    'IAS 16',
  'inventory-details': 'IAS 2',
  'lease-contracts':   'IFRS 16',
  'lease-payments':    'IFRS 16',
  'ecl-parameters':    'IFRS 9',
  'receivable-aging':  'IFRS 9',
};

const COLLECTOR_TABS = [
  {
    key: 'fx-rates',
    label: 'FX',
    labelTr: 'Döviz',
    statusPath: 'fx-rates/status',
    summaryField: 'currency',
    defaults: { rateType: 'PERIOD_END', currency: 'USD', rate: '1', rateDate: '2026-01-01', source: 'MANUAL' },
    fields: [
      { name: 'rateType', label: 'Rate Type', labelTr: 'Kur Tipi', type: 'select', options: ['PERIOD_END', 'AVERAGE', 'TRANSACTION'], optionsTr: ['Dönem Sonu', 'Ortalama', 'İşlem'], required: true },
      { name: 'currency', label: 'Currency', labelTr: 'Para Birimi', type: 'text', required: true },
      { name: 'rate', label: 'Rate', labelTr: 'Kur', type: 'number', required: true },
      { name: 'rateDate', label: 'Rate Date', labelTr: 'Kur Tarihi', type: 'date', required: true },
      { name: 'source', label: 'Source', labelTr: 'Kaynak', type: 'text' }
    ]
  },
  {
    key: 'tax-profiles',
    label: 'Tax',
    labelTr: 'Vergi',
    statusPath: 'tax-profiles/status',
    summaryField: 'corporateTaxRate',
    defaults: { corporateTaxRate: '0.25', futureTaxRate: '0.25', taxIncentives: '' },
    fields: [
      { name: 'corporateTaxRate', label: 'Corporate Tax Rate', labelTr: 'Kurumlar Vergisi Oranı', type: 'number', required: true },
      { name: 'futureTaxRate', label: 'Future Tax Rate', labelTr: 'Gelecek Dönem Vergi Oranı', type: 'number' },
      { name: 'taxIncentives', label: 'Tax Incentives', labelTr: 'Vergi Teşvikleri', type: 'text' }
    ]
  },
  {
    key: 'tax-bases',
    label: 'Tax Base',
    labelTr: 'Vergi Matrahı',
    statusPath: 'tax-bases/status',
    summaryField: 'itemCode',
    defaults: { itemCode: 'ITEM-1', taxBaseOfAsset: '1000', taxBaseOfLiability: '0' },
    fields: [
      { name: 'itemCode', label: 'Item Code', labelTr: 'Kalem Kodu', type: 'text', required: true },
      { name: 'taxBaseOfAsset', label: 'Tax Base (Asset)', labelTr: 'Vergi Matrahı (Varlık)', type: 'number' },
      { name: 'taxBaseOfLiability', label: 'Tax Base (Liability)', labelTr: 'Vergi Matrahı (Borç)', type: 'number' }
    ]
  },
  {
    key: 'asset-register',
    label: 'Assets',
    labelTr: 'Varlıklar',
    statusPath: 'asset-register/status',
    summaryField: 'assetId',
    defaults: {
      assetId: 'A-1',
      acquisitionDate: '2026-01-01',
      acquisitionCost: '1000',
      usefulLife: '5',
      depreciationMethod: 'STRAIGHT_LINE',
      residualValue: '0',
      componentStructure: ''
    },
    fields: [
      { name: 'assetId', label: 'Asset ID', labelTr: 'Varlık ID', type: 'text', required: true },
      { name: 'acquisitionDate', label: 'Acquisition Date', labelTr: 'Edinim Tarihi', type: 'date', required: true },
      { name: 'acquisitionCost', label: 'Acquisition Cost', labelTr: 'Edinim Maliyeti', type: 'number', required: true },
      { name: 'usefulLife', label: 'Useful Life (years)', labelTr: 'Faydalı Ömür (yıl)', type: 'int', required: true },
      { name: 'depreciationMethod', label: 'Depreciation Method', labelTr: 'Amortisman Yöntemi', type: 'select', options: ['STRAIGHT_LINE', 'DECLINING_BALANCE'], optionsTr: ['Doğrusal', 'Azalan Bakiye'], required: true },
      { name: 'residualValue', label: 'Residual Value', labelTr: 'Kalıntı Değer', type: 'number' },
      { name: 'componentStructure', label: 'Component Structure', labelTr: 'Bileşen Yapısı', type: 'text' }
    ]
  },
  {
    key: 'inventory-details',
    label: 'Inventory',
    labelTr: 'Stok',
    statusPath: 'inventory-details/status',
    summaryField: 'itemId',
    defaults: { itemId: 'INV-1', cost: '100', netRealizableValue: '95', quantity: '1', valuationMethod: 'FIFO' },
    fields: [
      { name: 'itemId', label: 'Item ID', labelTr: 'Kalem ID', type: 'text', required: true },
      { name: 'cost', label: 'Cost', labelTr: 'Maliyet', type: 'number', required: true },
      { name: 'netRealizableValue', label: 'Net Realizable Value', labelTr: 'Net Gerçekleşebilir Değer', type: 'number', required: true },
      { name: 'quantity', label: 'Quantity', labelTr: 'Miktar', type: 'number', required: true },
      { name: 'valuationMethod', label: 'Valuation Method', labelTr: 'Değerleme Yöntemi', type: 'select', options: ['FIFO', 'WEIGHTED_AVG', 'SPECIFIC'], optionsTr: ['FIFO', 'Ağırlıklı Ort.', 'Spesifik'], required: true }
    ]
  },
  {
    key: 'receivable-aging',
    label: 'ECL Aging',
    labelTr: 'Alacak Yaşlandırma',
    statusPath: 'receivable-aging/status',
    summaryField: 'customerId',
    defaults: { customerId: 'C-1', invoiceDate: '2026-01-01', dueDate: '2026-02-01', amount: '100', daysOverdue: '30' },
    fields: [
      { name: 'customerId', label: 'Customer ID', labelTr: 'Müşteri ID', type: 'text', required: true },
      { name: 'invoiceDate', label: 'Invoice Date', labelTr: 'Fatura Tarihi', type: 'date', required: true },
      { name: 'dueDate', label: 'Due Date', labelTr: 'Vade Tarihi', type: 'date', required: true },
      { name: 'amount', label: 'Amount', labelTr: 'Tutar', type: 'number', required: true },
      { name: 'daysOverdue', label: 'Days Overdue', labelTr: 'Gecikme Günü', type: 'int', required: true }
    ]
  },
  {
    key: 'ecl-parameters',
    label: 'ECL Param',
    labelTr: 'Beklenen Kredi Zararı Parametreleri',
    statusPath: 'ecl-parameters/status',
    summaryField: 'segmentCode',
    defaults: { segmentCode: 'DEFAULT', pd: '0.05', lgd: '0.50', ead: '100' },
    fields: [
      { name: 'segmentCode', label: 'Segment Code', labelTr: 'Segment Kodu', type: 'text', required: true },
      { name: 'pd', label: 'PD', labelTr: 'PD (Temerrüt Olasılığı)', type: 'number', required: true },
      { name: 'lgd', label: 'LGD', labelTr: 'LGD (Temerrüt Kaybı)', type: 'number', required: true },
      { name: 'ead', label: 'EAD', labelTr: 'EAD (Temerrüt Tutarı)', type: 'number', required: true }
    ]
  },
  {
    key: 'lease-contracts',
    label: 'Leases',
    labelTr: 'Finansal Kiralama',
    statusPath: 'lease-contracts/status',
    summaryField: 'contractId',
    defaults: { contractId: 'L-1', leaseTerm: '12', discountRate: '0.02', assetValue: '1000' },
    fields: [
      { name: 'contractId', label: 'Contract ID', labelTr: 'Sözleşme ID', type: 'text', required: true },
      { name: 'leaseTerm', label: 'Lease Term (period)', labelTr: 'Kira Süresi (dönem)', type: 'int', required: true },
      { name: 'discountRate', label: 'Discount Rate', labelTr: 'İskonto Oranı', type: 'number', required: true },
      { name: 'assetValue', label: 'Asset Value', labelTr: 'Varlık Değeri', type: 'number', required: true }
    ]
  },
  {
    key: 'lease-payments',
    label: 'Lease Pmts',
    labelTr: 'Finansal Kiralama Ödemeleri',
    statusPath: 'lease-payments/status',
    summaryField: 'contractId',
    defaults: { contractId: 'L-1', paymentNo: '1', paymentDate: '2026-01-31', paymentAmount: '100' },
    fields: [
      { name: 'contractId', label: 'Contract ID', labelTr: 'Sözleşme ID', type: 'text', required: true },
      { name: 'paymentNo', label: 'Payment No', labelTr: 'Ödeme No', type: 'int', required: true },
      { name: 'paymentDate', label: 'Payment Date', labelTr: 'Ödeme Tarihi', type: 'date', required: true },
      { name: 'paymentAmount', label: 'Payment Amount', labelTr: 'Ödeme Tutarı', type: 'number', required: true }
    ]
  }
];

// ─── Çeviri haritaları ───────────────────────────────────────────────────────

const STEP_LABELS_TR = {
  INGESTION: 'Mizan Aktarım',
  REVERSE: 'Ters Bakiye',
  PNL_CLOSE: 'GK Kapanış',
  BALANCE_CLOSE: 'Bilanço Kapanış',
  VUK_READY: 'VUK Hazır',
  IFRS_DATA_READY: 'IFRS Veri Hazır',
  IFRS_BATCH_READY: 'IFRS İşlem Grubu Hazır',
  DONE: 'Tamamlandı',
};

const STEP_LINKS = {
  INGESTION: '/data-ingestion/mizan-import',
  REVERSE: '/financial-analysis/reverse-balance',
  PNL_CLOSE: '/financial-analysis/pnl-closing',
  BALANCE_CLOSE: '/financial-analysis/balance-closing',
};

const QUALITY_STATUS_TR = {
  APPROVED: 'Onaylandı',
  PENDING: 'Beklemede',
  REJECTED: 'Reddedildi',
  DRAFT: 'Taslak',
};

const DATASET_LABELS_TR = {
  'fx-rates': 'Döviz Kurları',
  'tax-profiles': 'Vergi Profilleri',
  'tax-bases': 'Vergi Matrahları',
  'asset-register': 'Varlık Kaydı',
  'inventory-details': 'Stok Detayları',
  'receivable-aging': 'Alacak Yaşlandırma',
  'ecl-parameters': 'Beklenen Kredi Zararı Parametreleri',
  'lease-contracts': 'Finansal Kiralama Sözleşmeleri',
  'lease-payments': 'Finansal Kiralama Ödemeleri',
};

const getDatasetLabel = (datasetCode, isTr) => {
  const normalizedCode = String(datasetCode || '').replace(/_/g, '-');
  if (!isTr) return normalizedCode || '-';
  return (DATASET_LABELS_TR[normalizedCode] ?? normalizedCode) || '-';
};

// ─── Yardımcı fonksiyonlar ───────────────────────────────────────────────────

/**
 * key=value formatındaki ham adım notunu insanca okunabilir cümleye çevirir.
 * Türkçe modda şablonlu metin, diğer dillerde orijinal not döner.
 */
const renderStepNote = (note, isTr) => {
  if (!note) return '';
  if (!isTr) return note;

  const pairs = {};
  note.replace(/([a-z_]+)=(\S+)/g, (_, k, v) => { pairs[k] = v; });

  if (pairs.trial_balance_rows !== undefined)
    return `${pairs.trial_balance_rows} satır aktarıldı`;
  if (pairs.reverse_findings !== undefined)
    return pairs.reverse_findings === '0' ? 'Düzeltme gerekmiyor' : `${pairs.reverse_findings} bulgu giderilmeli`;
  if (pairs.pnl_findings !== undefined)
    return pairs.pnl_findings === '0' ? 'Düzeltme gerekmiyor' : `${pairs.pnl_findings} bulgu giderilmeli`;
  if (pairs.balance_findings !== undefined)
    return pairs.balance_findings === '0' ? 'Düzeltme gerekmiyor' : `${pairs.balance_findings} bulgu giderilmeli`;
  if (pairs.vuk_snapshot_rows !== undefined)
    return `${pairs.vuk_snapshot_rows} satır oluşturuldu`;
  if (pairs.missing !== undefined)
    return pairs.missing === '0' ? 'Tüm veriler eksiksiz' : `${pairs.missing} eksik veri seti`;
  if (pairs.success_batch !== undefined)
    return pairs.success_batch === 'true' ? 'Hesaplama tamamlandı' : 'Hesaplama başarısız';
  if (pairs.approved_batch !== undefined)
    return pairs.approved_batch === 'true' ? 'Onaylandı' : 'Onay bekleniyor';

  return note;
};

const mapBatchStatus = (status, isTr) => {
  const key = String(status || '').toUpperCase();
  if (!isTr) return key || '-';
  const map = {
    RUNNING: 'Çalışıyor', SUCCESS: 'Başarılı', FAILED: 'Başarısız',
    APPROVED: 'Onaylandı', PENDING: 'Beklemede',
  };
  return map[key] || key || '-';
};

const toPayloadValue = (field, rawValue) => {
  const value = rawValue ?? '';
  if (value === '') return field.required ? null : undefined;
  if (field.type === 'number') {
    const normalized = String(value).replace(',', '.');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (field.type === 'int') {
    const parsed = parseInt(String(value), 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return value;
};

const parseAccountPayload = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload.filter(Boolean);
  try {
    const parsed = JSON.parse(payload);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
};

// ─── Bileşen ─────────────────────────────────────────────────────────────────

function VukIfrsTransformationWizardPage() {
  const { language } = useLanguage();
  const isTr = !String(language || '').toLowerCase().startsWith('en');
  const L = useCallback((en, tr) => (isTr ? tr : en), [isTr]);

  const [selection, setSelection] = useState({ companyId: '', periodId: '' });
  const [selectionMeta, setSelectionMeta] = useState({ companyName: '', periodName: '' });
  const [workflow, setWorkflow] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [runBatchResult, setRunBatchResult] = useState(null);
  const [batchApproving, setBatchApproving] = useState(false);
  const [isChangingPeriod, setIsChangingPeriod] = useState(false);
  const [comparisonSummary, setComparisonSummary] = useState(null);
  const [comparisonLines, setComparisonLines] = useState([]);
  const [strictMode, setStrictMode] = useState(true);
  const [activeTab, setActiveTab] = useState(COLLECTOR_TABS[0].key);
  const [standardsByCode, setStandardsByCode] = useState({});
  const [tabRows, setTabRows] = useState([]);
  const [formValues, setFormValues] = useState(COLLECTOR_TABS[0].defaults);
  const [editingRowId, setEditingRowId] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const { canWrite } = useCompanyWriteAccess(selection.companyId);

  const collectorRef = useRef(null);

  const hasSelection = Boolean(selection.companyId && selection.periodId);

  const activeTabConfig = useMemo(
    () => COLLECTOR_TABS.find((x) => x.key === activeTab) || COLLECTOR_TABS[0],
    [activeTab]
  );

  const isStepReached = useCallback(
    (stepName) => workflow?.steps?.find((s) => s.step === stepName)?.completed ?? false,
    [workflow]
  );

  const clearStatus = () => {
    setMessage('');
    setError('');
  };

  const resetWizardView = useCallback(() => {
    setWorkflow(null);
    setReadiness(null);
    setRunBatchResult(null);
    setComparisonSummary(null);
    setComparisonLines([]);
    setTabRows([]);
  }, []);

  const resetCollectorForm = useCallback(() => {
    setEditingRowId(null);
    setFormValues(activeTabConfig.defaults);
  }, [activeTabConfig.defaults]);

  const buildCollectorFormValues = useCallback((row) => {
    const nextValues = { ...activeTabConfig.defaults };
    activeTabConfig.fields.forEach((field) => {
      const rawValue = row?.[field.name];
      if (rawValue === undefined || rawValue === null) {
        nextValues[field.name] = activeTabConfig.defaults[field.name] ?? '';
        return;
      }
      nextValues[field.name] = String(rawValue);
    });
    return nextValues;
  }, [activeTabConfig]);

  // Şirket/dönem seçici değiştiğinde meta bilgileri de sakla
  const handleSelectionChange = useCallback((sel) => {
    const nextSelection = { companyId: sel.companyId || '', periodId: sel.periodId || '' };
    // Her iki değer de seçilmeden selection state'ini güncelleme; aksi hâlde
    // initialCompanyId/initialPeriodId prop'ları değişerek CompanyPeriodSelector'ı
    // yeniden başlatır ve döngüye yol açar.
    if (!nextSelection.companyId || !nextSelection.periodId) return;
    setSelectionMeta({
      companyName: sel.company?.name ?? sel.company?.companyName ?? sel.companyId ?? '',
      periodName: sel.period?.name ?? sel.period?.periodName ?? sel.periodId ?? '',
    });
    setSelection((prev) => {
      const selectionChanged = nextSelection.companyId !== prev.companyId
        || nextSelection.periodId !== prev.periodId;
      if (!selectionChanged) {
        return prev;
      }
      resetWizardView();
      setIsChangingPeriod(false);
      return nextSelection;
    });
  }, [resetWizardView]);

  const refreshWorkflow = useCallback(async () => {
    if (!hasSelection) return;
    clearStatus();
    try {
      const [workflowData, readinessData] = await Promise.all([
        ifrsService.fetchIfrsWorkflowStatus(selection),
        ifrsService.fetchIfrsReadiness(selection)
      ]);
      setWorkflow(workflowData);
      setReadiness(readinessData);
    } catch (caught) {
      setError(caught.message || L('Could not load workflow/readiness.', 'Workflow/hazırlık bilgisi alınamadı.'));
    }
  }, [L, hasSelection, selection]);

  const refreshTabRows = useCallback(async () => {
    if (!hasSelection) return;
    clearStatus();
    try {
      const rows = await ifrsService.listCollectorDataset({ path: activeTabConfig.key, ...selection });
      setTabRows(rows || []);
    } catch (caught) {
      setError(caught.message || L('Could not load collector list.', 'Toplayıcı listesi alınamadı.'));
    }
  }, [L, activeTabConfig.key, hasSelection, selection]);

  const refreshComparison = useCallback(async () => {
    if (!hasSelection) return;
    clearStatus();
    try {
      const [summary, lines] = await Promise.all([
        ifrsService.fetchVukIfrsComparisonSummary(selection),
        ifrsService.fetchVukIfrsComparisonLines(selection)
      ]);
      setComparisonSummary(summary);
      setComparisonLines(lines || []);
    } catch (caught) {
      setError(caught.message || L('Could not load comparison.', 'Karşılaştırma alınamadı.'));
    }
  }, [L, hasSelection, selection]);

  useEffect(() => {
    if (!hasSelection) return;
    refreshWorkflow();
  }, [hasSelection, refreshWorkflow]);

  useEffect(() => {
    fetchActiveIfrsStandards()
      .then(data => {
        const map = {};
        (data || []).forEach(s => { map[s.standardCode] = s; });
        setStandardsByCode(map);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    resetCollectorForm();
    if (hasSelection) {
      refreshTabRows();
    }
  }, [activeTabConfig, hasSelection, refreshTabRows, resetCollectorForm]);

  // ─── İşleyiciler ────────────────────────────────────────────────────────────

  const handleAdvance = async () => {
    if (!canWrite) {
      setError(L('You do not have write access for this company.', 'Bu sirket icin yazma yetkiniz yok.'));
      return;
    }
    if (!hasSelection) return;
    clearStatus();
    try {
      const data = await ifrsService.advanceIfrsWorkflow(selection);
      setWorkflow(data);
      setReadiness(data.readiness);
      const stepLabel = isTr ? (STEP_LABELS_TR[data.currentStep] ?? data.currentStep) : data.currentStep;
      setMessage(L(`Workflow advanced → ${data.currentStep}`, `Sonraki adıma geçildi → ${stepLabel}`));
    } catch (caught) {
      setError(caught.message || L('Workflow could not be advanced.', 'Workflow ilerletilemedi.'));
    }
  };

  const handleReset = async () => {
    if (!canWrite) {
      setError(L('You do not have write access for this company.', 'Bu sirket icin yazma yetkiniz yok.'));
      return;
    }
    if (!hasSelection) return;
    clearStatus();
    try {
      const data = await ifrsService.resetIfrsWorkflow(selection);
      setWorkflow(data);
      setReadiness(data.readiness);
      setRunBatchResult(null);
      setMessage(L('Workflow reset.', 'Workflow sıfırlandı.'));
    } catch (caught) {
      setError(caught.message || L('Workflow could not be reset.', 'Workflow sıfırlanamadı.'));
    }
  };

  const handleUnlockPeriod = () => {
    if (!window.confirm(L(
      'Changing the period will reset the workflow view. Continue?',
      'Dönem değiştirilirse bu ekrandaki workflow görünümü sıfırlanır. Devam edilsin mi?'
    ))) return;
    clearStatus();
    setIsChangingPeriod(true);
  };

  const handleAddCollectorRow = async () => {
    if (!canWrite) {
      setError(L('You do not have write access for this company.', 'Bu sirket icin yazma yetkiniz yok.'));
      return;
    }
    if (!hasSelection) return;
    clearStatus();
    try {
      const payload = {};
      for (const field of activeTabConfig.fields) {
        const converted = toPayloadValue(field, formValues[field.name]);
        if (converted === undefined) continue;
        if (converted === null && field.required) {
          setError(L(
            `Required field missing/invalid: ${field.label}`,
            `Zorunlu alan eksik/geçersiz: ${field.labelTr}`
          ));
          return;
        }
        if (converted !== null) {
          payload[field.name] = converted;
        }
      }
      if (editingRowId) {
        await ifrsService.updateCollectorDataset({ path: activeTabConfig.key, ...selection, id: editingRowId, payload });
      } else {
        await ifrsService.addCollectorDataset({ path: activeTabConfig.key, ...selection, payload });
      }
      setMessage(editingRowId
        ? L(
            `${activeTabConfig.label} record updated. Approval is required again.`,
            `${activeTabConfig.labelTr} kaydi guncellendi. Yeniden onay gerekir.`
          )
        : L(
            `${activeTabConfig.label} record added.`,
        `${activeTabConfig.labelTr} kaydı eklendi.`
          ));
      resetCollectorForm();
      await refreshTabRows();
      await refreshWorkflow();
    } catch (caught) {
      setError(caught.message || L('Could not add collector record.', 'Toplayıcı kaydı eklenemedi.'));
    }
  };

  const handleEditRow = (row) => {
    clearStatus();
    setEditingRowId(row.id);
    setFormValues(buildCollectorFormValues(row));
  };

  const handleDeleteRow = async (row) => {
    if (!canWrite) {
      setError(L('You do not have write access for this company.', 'Bu sirket icin yazma yetkiniz yok.'));
      return;
    }
    if (!hasSelection) return;
    if (!window.confirm(L(`Delete record #${row.id}?`, `#${row.id} numarali kayit silinsin mi?`))) return;
    clearStatus();
    try {
      await ifrsService.deleteCollectorDataset({ path: activeTabConfig.key, ...selection, id: row.id });
      if (editingRowId === row.id) {
        resetCollectorForm();
      }
      setMessage(L(`Record deleted (#${row.id}).`, `Kayit silindi (#${row.id}).`));
      await refreshTabRows();
      await refreshWorkflow();
    } catch (caught) {
      setError(caught.message || L('Record could not be deleted.', 'Kayit silinemedi.'));
    }
  };

  const handleApproveRow = async (id) => {
    if (!canWrite) {
      setError(L('You do not have write access for this company.', 'Bu sirket icin yazma yetkiniz yok.'));
      return;
    }
    if (!hasSelection || !activeTabConfig.statusPath) return;
    clearStatus();
    try {
      await ifrsService.updateCollectorStatus({ path: activeTabConfig.key, ...selection, id, qualityStatus: 'APPROVED' });
      setMessage(L(`Record approved (#${id}).`, `Kayıt onaylandı (#${id}).`));
      await refreshTabRows();
      await refreshWorkflow();
    } catch (caught) {
      setError(caught.message || L('Record could not be approved.', 'Kayıt onaylanamadı.'));
    }
  };

  const handleApproveAll = async () => {
    if (!canWrite) {
      setError(L('You do not have write access for this company.', 'Bu sirket icin yazma yetkiniz yok.'));
      return;
    }
    if (!hasSelection || !activeTabConfig.statusPath) return;
    clearStatus();
    const pending = tabRows.filter((r) => r.qualityStatus !== 'APPROVED');
    if (pending.length === 0) return;
    try {
      for (const row of pending) {
        await ifrsService.updateCollectorStatus({
          path: activeTabConfig.key, ...selection, id: row.id, qualityStatus: 'APPROVED'
        });
      }
      setMessage(L(`${pending.length} record(s) approved.`, `${pending.length} kayıt onaylandı.`));
      await refreshTabRows();
      await refreshWorkflow();
    } catch (caught) {
      setError(caught.message || L('Bulk approval failed.', 'Toplu onaylama başarısız.'));
    }
  };

  const handleGenerateVukSnapshot = async () => {
    if (!canWrite) {
      setError(L('You do not have write access for this company.', 'Bu sirket icin yazma yetkiniz yok.'));
      return;
    }
    if (!hasSelection) return;
    clearStatus();
    try {
      const result = await ifrsService.generateVukSnapshot(selection);
      const count = typeof result === 'number' ? result : result?.createdRows;
      setMessage(L(`VUK snapshot generated: ${count ?? 0} rows`, `VUK snapshot oluşturuldu: ${count ?? 0} satır`));
      await refreshWorkflow();
    } catch (caught) {
      setError(caught.message || L('VUK snapshot could not be generated.', 'VUK snapshot oluşturulamadı.'));
    }
  };

  const handleRunBatch = async () => {
    if (!canWrite) {
      setError(L('You do not have write access for this company.', 'Bu sirket icin yazma yetkiniz yok.'));
      return;
    }
    if (!hasSelection) return;
    clearStatus();
    try {
      const readinessState = await ifrsService.fetchIfrsReadiness(selection);
      setReadiness(readinessState);
      if (!readinessState.ready) {
        return; // Eksik veri linki zaten UI'da gösterilecek
      }
      const result = await ifrsService.runIfrsBatch({
        ...selection,
        ruleSetVersion: 'v2',
        calculationEngineVersion: 'v2',
        strictMode
      });
      setRunBatchResult(result);
      setMessage(result.message || L(`IFRS batch started: #${result.batchId}`, `IFRS işlem grubu başlatıldı: #${result.batchId}`));
      await refreshWorkflow();
    } catch (caught) {
      const msg = caught.message || L('IFRS batch could not be started.', 'IFRS işlem grubu başlatılamadı.');
      if (msg.includes('Strict mapping mode aktif')) {
        const fallbackAccounts = Number(caught.payload?.fallbackAccounts || 0);
        const unmappedAccounts = Number(caught.payload?.unmappedAccounts || 0);
        const fallbackList = parseAccountPayload(caught.payload?.fallbackAccountsPayload).slice(0, 5);
        const unmappedList = parseAccountPayload(caught.payload?.unmappedAccountsPayload).slice(0, 5);
        const details = L(
          [
            unmappedAccounts > 0 ? `${unmappedAccounts} unmapped account(s)` : '',
            fallbackAccounts > 0 ? `${fallbackAccounts} fallback account(s)` : '',
            unmappedList.length > 0 ? `Unmapped examples: ${unmappedList.join(', ')}` : '',
            fallbackList.length > 0 ? `Fallback examples: ${fallbackList.join(', ')}` : ''
          ].filter(Boolean).join(' | '),
          [
            unmappedAccounts > 0 ? `${unmappedAccounts} eşleştirilmemiş hesap` : '',
            fallbackAccounts > 0 ? `${fallbackAccounts} varsayılan kategoriye düşen hesap` : '',
            unmappedList.length > 0 ? `Eşleştirilmemiş örnekler: ${unmappedList.join(', ')}` : '',
            fallbackList.length > 0 ? `Fallback örnekleri: ${fallbackList.join(', ')}` : ''
          ].filter(Boolean).join(' | ')
        );
        setError(L(
          `Batch stopped because strict mapping mode is enabled. Complete the mappings or switch to Continue mode and try again.${details ? ` ${details}` : ''}`,
          `İşlem grubu, "Dur ve Uyar" modu açık olduğu için durduruldu. Eşleştirmeleri tamamlayın veya "Devam et" moduna geçerek tekrar deneyin.${details ? ` ${details}` : ''}`
        ));
        return;
      }
      setError(msg);
    }
  };

  const handleApproveBatchInWizard = async () => {
    if (!canWrite) {
      setError(L('You do not have write access for this company.', 'Bu sirket icin yazma yetkiniz yok.'));
      return;
    }
    if (!hasSelection || !runBatchResult?.batchId) return;
    clearStatus();
    setBatchApproving(true);
    try {
      const data = await ifrsService.approveIfrsBatch({
        batchId: runBatchResult.batchId,
        companyId: selection.companyId,
        periodId: selection.periodId,
      });
      setRunBatchResult(data);
      await refreshWorkflow();
      setMessage(L(`Batch approved: #${data.batchId}`, `İşlem grubu onaylandı: #${data.batchId}`));
    } catch (caught) {
      setError(caught.message || L('Batch approval failed.', 'İşlem grubu onaylanamadı.'));
    } finally {
      setBatchApproving(false);
    }
  };

  const handleDownload = async (type, format) => {
    if (!hasSelection) return;
    clearStatus();
    try {
      const blob = type === 'report'
        ? await ifrsService.downloadIfrsReportExport({ ...selection, reportType: 'financialPosition', format })
        : await ifrsService.downloadComparisonExport({ ...selection, format });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = type === 'report'
        ? `ifrs-report.${format === 'excel' ? 'xlsx' : 'pdf'}`
        : `vuk-ifrs-comparison.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (caught) {
      setError(caught.message || L('File could not be downloaded.', 'Dosya indirilemedi.'));
    }
  };

  // ─── Türetilmiş değerler ─────────────────────────────────────────────────────

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(isTr ? 'tr-TR' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    [isTr]
  );

  const formatNumber = useCallback((value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value ?? '-');
    return numberFormatter.format(n);
  }, [numberFormatter]);

  const displayFieldValue = useCallback((field, value) => {
    if (isTr && field.type === 'number' && value != null) {
      return String(value).replace('.', ',');
    }
    return String(value ?? '');
  }, [isTr]);

  const summaryFieldLabel = useMemo(() => {
    const field = activeTabConfig.fields.find((f) => f.name === activeTabConfig.summaryField);
    return field ? (isTr ? field.labelTr : field.label) : 'ID';
  }, [activeTabConfig, isTr]);

  const hasPendingRows = activeTabConfig.statusPath
    && tabRows.some((r) => r.qualityStatus !== 'APPROVED');

  const collectorLocked = workflow !== null && !isStepReached('VUK_READY');
  const readinessLocked = workflow !== null && !isStepReached('VUK_READY');
  const outputsLocked   = workflow !== null && !isStepReached('DONE');

  // ─── JSX ─────────────────────────────────────────────────────────────────────

  return (
    <div className="vuk-ifrs-wizard">
      <PageHeader
        title={L('VUK → IFRS/TMS Transformation Wizard', 'VUK → IFRS/TMS Dönüşüm Sihirbazı')}
        description={L(
          'Follow the guided workflow from mizan import to IFRS report export in a single flow.',
          'Mizan aktarımından IFRS rapor/dışa aktarmaya kadar tek akışta ilerleyin.'
        )}
      />

      {/* Dönem seçici — workflow yüklüyse kilit çubuğu göster */}
      {workflow && !isChangingPeriod ? (
        <div className="period-lock-bar">
          <span className="period-lock-label">
            {selectionMeta.companyName || selection.companyId}
            {' / '}
            {selectionMeta.periodName || selection.periodId}
          </span>
          <button type="button" className="btn-secondary period-change-btn" onClick={handleUnlockPeriod}>
            {L('Change Period', 'Dönemi Değiştir')}
          </button>
        </div>
      ) : (
        <>
          <CompanyPeriodSelector
            onSelectionChange={handleSelectionChange}
            initialCompanyId={selection.companyId}
            initialPeriodId={selection.periodId}
            autoSelectFirst={!isChangingPeriod}
          />
          {isChangingPeriod ? (
            <div className="wizard-actions" style={{ marginTop: '0.75rem' }}>
              <button type="button" className="btn-secondary" onClick={() => setIsChangingPeriod(false)}>
                {L('Cancel', 'Vazgec')}
              </button>
            </div>
          ) : null}
        </>
      )}

      {message ? <div className="wizard-banner success">{message}</div> : null}
      {error ? <div className="wizard-banner error">{error}</div> : null}

      {/* ── Adım Gezgini ──────────────────────────────────────────────────── */}
      <section className="card wizard-card">
        <h2>{L('Progress', 'İlerleme Durumu')}</h2>

        <div className="wizard-actions">
          <button type="button" className="btn-secondary" onClick={refreshWorkflow} disabled={!hasSelection}>
            {L('Refresh', 'Yenile')}
          </button>
          <button type="button" className="btn-primary" onClick={handleAdvance} disabled={!canWrite || !hasSelection}>
            {L('Next Step →', 'Sonraki Adım →')}
          </button>
          <button type="button" className="btn-danger" onClick={handleReset} disabled={!canWrite || !hasSelection}>
            {L('Reset', 'Sıfırla')}
          </button>
        </div>

        {workflow ? (
          <div className="step-indicator">
            {workflow.steps.map((step, idx) => {
              const isActive = workflow.currentStep === step.step;
              const isDone = step.completed;
              const stepLink = STEP_LINKS[step.step];
              const label = isTr ? (STEP_LABELS_TR[step.step] ?? step.step) : step.step;
              const noteText = renderStepNote(step.note, isTr);
              const stateClass = isDone ? 'step-item--done' : isActive ? 'step-item--active' : 'step-item--locked';

              return (
                <div key={step.step} className={`step-item ${stateClass}`}>
                  <div className="step-icon">{isDone ? '✓' : idx + 1}</div>
                  <div className="step-body">
                    <div className="step-name">
                      {label}
                      {stepLink && (
                        <Link to={stepLink} className="step-goto">
                          {L('Go →', 'Git →')}
                        </Link>
                      )}
                    </div>
                    {noteText ? <div className="step-note">{noteText}</div> : null}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="muted">{L('Select a company and period to load the workflow.', 'Workflow yüklemek için şirket ve dönem seçin.')}</p>
        )}
      </section>

      {/* ── IFRS Veri Toplayıcı ───────────────────────────────────────────── */}
      <section
        ref={collectorRef}
        className={`card wizard-card${collectorLocked ? ' wizard-card--locked' : ''}`}
      >
        {collectorLocked && (
          <div className="wizard-lock-banner">
            {L('🔒 Complete the VUK Snapshot step to access this section.', '🔒 Bu bölüme erişmek için VUK Snapshot adımını tamamlayın.')}
          </div>
        )}
        <h2>{L('IFRS Data Collector', 'IFRS Veri Toplayıcı')}</h2>
        <div className="wizard-tabs">
          {COLLECTOR_TABS.map((tab) => {
            const stdCode = COLLECTOR_TAB_STANDARDS[tab.key];
            const std = stdCode ? standardsByCode[stdCode] : null;
            return (
              <button
                key={tab.key}
                type="button"
                className={tab.key === activeTab ? 'tab active' : 'tab'}
                onClick={() => setActiveTab(tab.key)}
              >
                {isTr ? tab.labelTr : tab.label}
                {std && (
                  <a
                    href={std.kgkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wizard-std-badge"
                    title={isTr ? std.titleTr : std.titleEn}
                    onClick={e => e.stopPropagation()}
                  >
                    {stdCode}
                  </a>
                )}
              </button>
            );
          })}
        </div>

        <div className="wizard-grid">
          <div>
            <p>
              <strong>{L('Dataset:', 'Veri Seti:')}</strong>{' '}
              {isTr ? (DATASET_LABELS_TR[activeTabConfig.key] ?? activeTabConfig.key) : activeTabConfig.key}
            </p>
            {editingRowId ? (
              <p className="collector-form-mode">
                {L(`Editing record #${editingRowId}`, `Kayit duzenleniyor #${editingRowId}`)}
              </p>
            ) : null}
            <div className="collector-form-grid">
              {activeTabConfig.fields.map((field) => (
                <label key={field.name} className="collector-field">
                  <span>{isTr ? field.labelTr : field.label}{field.required ? ' *' : ''}</span>
                  {field.type === 'select' ? (
                    <select
                      value={formValues[field.name] ?? ''}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
                      disabled={!canWrite}
                    >
                      {field.options.map((option, optIdx) => (
                        <option key={option} value={option}>
                          {isTr ? (field.optionsTr?.[optIdx] ?? option) : option}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type === 'date' ? 'date' : 'text'}
                      value={displayFieldValue(field, formValues[field.name])}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
                      disabled={!canWrite}
                    />
                  )}
                </label>
              ))}
            </div>
            <div className="wizard-actions">
              <button type="button" className="btn-primary" onClick={handleAddCollectorRow} disabled={!canWrite || !hasSelection}>
                {editingRowId ? L('Update Row', 'Satiri Guncelle') : L('Add Row', 'Satir Ekle')}
              </button>
              {editingRowId ? (
                <button type="button" className="btn-secondary" onClick={resetCollectorForm} disabled={!canWrite || !hasSelection}>
                  {L('Cancel Edit', 'Duzenlemeyi Iptal Et')}
                </button>
              ) : null}
              <button type="button" className="btn-secondary" onClick={refreshTabRows} disabled={!hasSelection}>
                {L('Refresh Rows', 'Satirlari Yenile')}
              </button>
            </div>
          </div>

          <div>
            {activeTabConfig.statusPath && tabRows.length > 0 && (
              <div className="wizard-actions" style={{ marginBottom: '0.5rem' }}>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleApproveAll}
                  disabled={!canWrite || !hasSelection || !hasPendingRows}
                >
                  {L('Approve All Pending', 'Tümünü Onayla')}
                </button>
              </div>
            )}
            <table className="wizard-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{summaryFieldLabel}</th>
                  <th>{L('Status', 'Durum')}</th>
                  {activeTabConfig.statusPath ? <th>{L('Action', 'İşlem')}</th> : null}
                </tr>
              </thead>
              <tbody>
                {tabRows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{row[activeTabConfig.summaryField] ?? '-'}</td>
                    <td>{isTr ? (QUALITY_STATUS_TR[row.qualityStatus] ?? row.qualityStatus ?? '-') : (row.qualityStatus || '-')}</td>
                    {activeTabConfig.statusPath ? (
                      <td className="collector-row-actions">
                        {row.qualityStatus !== 'APPROVED' ? (
                          <button type="button" className="btn-secondary" onClick={() => handleApproveRow(row.id)} disabled={!canWrite}>
                            {L('Approve', 'Onayla')}
                          </button>
                        ) : (
                          <span className="approved-check">✓</span>
                        )}
                        <button type="button" className="btn-secondary" onClick={() => handleEditRow(row)} disabled={!canWrite}>
                          {L('Edit', 'Duzenle')}
                        </button>
                        <button type="button" className="btn-secondary" onClick={() => handleDeleteRow(row)} disabled={!canWrite}>
                          {L('Delete', 'Sil')}
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── Hazırlık ve Çalıştırma ────────────────────────────────────────── */}
      <section className={`card wizard-card${readinessLocked ? ' wizard-card--locked' : ''}`}>
        {readinessLocked && (
          <div className="wizard-lock-banner">
            {L('🔒 Complete the VUK Snapshot step to access this section.', '🔒 Bu bölüme erişmek için VUK Snapshot adımını tamamlayın.')}
          </div>
        )}
        <h2>{L('Readiness and Run', 'Hazırlık ve Çalıştırma')}</h2>

        <label className="collector-field" style={{ maxWidth: '340px', marginBottom: '12px' }}>
          <span>{L('If unmapped accounts are found:', 'Eşleştirilmemiş hesap bulunursa:')}</span>
          <select value={strictMode ? 'STRICT' : 'FLEXIBLE'} onChange={(e) => setStrictMode(e.target.value === 'STRICT')} disabled={!canWrite}>
            <option value="STRICT">{L('Halt and warn (recommended)', 'Dur ve uyar (önerilen)')}</option>
            <option value="FLEXIBLE">{L('Continue with default category', 'Varsayılan kategoriyle devam et')}</option>
          </select>
        </label>

        <div className="wizard-actions">
          <button type="button" className="btn-secondary" onClick={handleGenerateVukSnapshot} disabled={!canWrite || !hasSelection}>
            {L('Generate VUK Snapshot', 'VUK Snapshot Oluştur')}
          </button>
          <button type="button" className="btn-secondary" onClick={refreshWorkflow} disabled={!hasSelection}>
            {L('Refresh Readiness', 'Hazırlığı Yenile')}
          </button>
          <button type="button" className="btn-primary" onClick={handleRunBatch} disabled={!canWrite || !hasSelection}>
            {L('Run IFRS Batch', 'IFRS İşlem Grubu Çalıştır')}
          </button>
        </div>

        {/* Hazırlık durumu ve eksik veri linkleri */}
        {readiness ? (
          <div className="readiness-status">
            <p>
              <strong>{L('Ready:', 'Hazır:')}</strong>{' '}
              <span className={readiness.ready ? 'readiness-ok' : 'readiness-fail'}>
                {readiness.ready ? L('YES ✓', 'EVET ✓') : L('NO', 'HAYIR')}
              </span>
            </p>
            {!readiness.ready && readiness.missingDatasets?.length > 0 && (
              <div className="missing-datasets">
                <span className="missing-datasets-label">
                  {L('Missing data — click to go to that section:', 'Eksik veriler — ilgili sekmeye gitmek için tıklayın:')}
                </span>
                <div className="missing-dataset-buttons">
                  {readiness.missingDatasets.map((x) => (
                    <button
                      key={x.missingDatasetCode}
                      type="button"
                      className="btn-secondary missing-dataset-btn"
                      onClick={() => {
                        setActiveTab(String(x.missingDatasetCode || '').replace(/_/g, '-'));
                        collectorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                    >
                      {getDatasetLabel(x.missingDatasetCode, isTr)} →
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}

        {/* Batch sonucu ve onay */}
        {runBatchResult ? (
          <div className="batch-result-bar">
            <div className="batch-result-info">
              <span>Batch <strong>#{runBatchResult.batchId}</strong></span>
              <span className={`batch-status-badge batch-status-badge--${String(runBatchResult.status || '').toLowerCase()}`}>
                {mapBatchStatus(runBatchResult.status, isTr)}
              </span>
              {runBatchResult.rowCount != null && (
                <span>{runBatchResult.rowCount} {L('rows', 'satır')}</span>
              )}
            </div>
            {runBatchResult.status !== 'APPROVED' ? (
              <button
                type="button"
                className="btn-primary"
                onClick={handleApproveBatchInWizard}
                disabled={!canWrite || batchApproving || !hasSelection}
              >
                {batchApproving
                  ? L('Approving...', 'Onaylanıyor...')
                  : L('Approve Batch', 'İşlem Grubunu Onayla')}
              </button>
            ) : (
              <span className="batch-approved-badge">✓ {L('Approved', 'Onaylandı')}</span>
            )}
          </div>
        ) : null}
      </section>

      {/* ── Çıktılar ve Karşılaştırma ─────────────────────────────────────── */}
      <section className={`card wizard-card${outputsLocked ? ' wizard-card--locked' : ''}`}>
        {outputsLocked && (
          <div className="wizard-lock-banner">
            {L('🔒 Approve the batch to unlock outputs.', '🔒 Çıktılara erişmek için işlem grubunu onaylayın.')}
          </div>
        )}
        <h2>{L('Outputs and Comparison', 'Çıktılar ve Karşılaştırma')}</h2>
        <div className="wizard-actions">
          <button type="button" className="btn-secondary" onClick={refreshComparison} disabled={!hasSelection}>
            {L('Load VUK-IFRS Comparison', 'VUK-IFRS Karşılaştırmasını Yükle')}
          </button>
          <button type="button" className="btn-secondary" onClick={() => handleDownload('report', 'excel')} disabled={!hasSelection}>
            {L('IFRS Excel', 'IFRS Excel')}
          </button>
          <button type="button" className="btn-secondary" onClick={() => handleDownload('report', 'pdf')} disabled={!hasSelection}>
            {L('IFRS PDF', 'IFRS PDF')}
          </button>
          <button type="button" className="btn-secondary" onClick={() => handleDownload('comparison', 'excel')} disabled={!hasSelection}>
            {L('Comparison Excel', 'Karşılaştırma Excel')}
          </button>
          <button type="button" className="btn-secondary" onClick={() => handleDownload('comparison', 'pdf')} disabled={!hasSelection}>
            {L('Comparison PDF', 'Karşılaştırma PDF')}
          </button>
        </div>
        {comparisonSummary ? (
          <div className="wizard-summary">
            <span>VUK: {formatNumber(comparisonSummary.vukTotal)}</span>
            <span>IFRS: {formatNumber(comparisonSummary.ifrsTotal)}</span>
            <span>Delta: {formatNumber(comparisonSummary.delta)}</span>
            <span>Delta%: {comparisonSummary.deltaPct}</span>
          </div>
        ) : null}
        {comparisonLines.length > 0 ? (
          <table className="wizard-table">
            <thead>
              <tr>
                <th>{L('Type', 'Tür')}</th>
                <th>{L('Code', 'Kod')}</th>
                <th>{L('Name', 'Ad')}</th>
                <th>VUK</th>
                <th>IFRS</th>
                <th>Delta</th>
                <th>Delta%</th>
              </tr>
            </thead>
            <tbody>
              {comparisonLines.map((line) => (
                <tr key={`${line.lineType}-${line.lineCode}`}>
                  <td>{line.lineType}</td>
                  <td>{line.lineCode}</td>
                  <td>{line.lineName}</td>
                  <td>{formatNumber(line.vukAmount)}</td>
                  <td>{formatNumber(line.ifrsAmount)}</td>
                  <td>{formatNumber(line.delta)}</td>
                  <td>{line.deltaPct}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>
    </div>
  );
}

export default VukIfrsTransformationWizardPage;

